import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import qs from 'qs'
import styled from 'styled-components'

import { BouncingLoader } from 'examkrackers-components'
import QuizDiagnosticsChaptersGrid from 'modules/quizDiagnostics/components/QuizDiagnosticsChaptersGrid'
import QuizDiagnosticsChapterSectionsGrid from 'modules/quizDiagnostics/chapterView/components/QuizDiagnosticsChapterSectionsGrid'
import QuizDiagnosticsEmptyState from 'modules/quizDiagnostics/components/QuizDiagnosticsEmptyState'
import QuizDiagnosticsHeader from 'modules/quizDiagnostics/components/QuizDiagnosticsHeader'
import QuizDiagnosticsNoAccessState from 'modules/quizDiagnostics/components/QuizDiagnosticsNoAccessState'
import { useQuizDiagnosticsChapterViewData } from 'modules/quizDiagnostics/chapterView/hooks/useQuizDiagnosticsChapterViewData'
import { useQuizDiagnosticsData } from 'modules/quizDiagnostics/hooks/useQuizDiagnosticsData'
import { useQuizDiagnosticsMobileRedirect } from 'modules/quizDiagnostics/hooks/useQuizDiagnosticsMobileRedirect'
import { QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH } from 'modules/quizDiagnostics/utils/quizDiagnosticsBreakpoints'
import { QUIZ_DIAGNOSTICS_PARAMS } from 'modules/quizDiagnostics/utils/quizDiagnosticsParams'
import PATHS from 'utils/paths'

const QuizDiagnostics = (): JSX.Element | null => {
  const { t } = useTranslation()
  const {
    replace,
    location: { pathname, search }
  } = useHistory()
  const isUnsupportedViewport = useQuizDiagnosticsMobileRedirect()

  const {
    bookGroups,
    summary,
    isLoading,
    hasError,
    srqEnabled,
    selectedChapterId,
    chapterFilterOptions,
    refetch: refetchChapterAverages
  } = useQuizDiagnosticsData()

  const isAllChaptersSelected = selectedChapterId === 'all'
  const chapterViewChapterId = isAllChaptersSelected ? '' : selectedChapterId
  const chapterIdFromQuery = qs.parse(search, { ignoreQueryPrefix: true })[
    QUIZ_DIAGNOSTICS_PARAMS.chapterId
  ]
  const hasChapterInQuery =
    typeof chapterIdFromQuery === 'string' && chapterIdFromQuery.length > 0

  useEffect(() => {
    if (!hasChapterInQuery && pathname === PATHS.sectionAverages) {
      replace(PATHS.chapterAverages)
      return
    }

    if (hasChapterInQuery && pathname === PATHS.chapterAverages) {
      replace(`${PATHS.sectionAverages}${search}`)
    }
  }, [hasChapterInQuery, pathname, replace, search])

  useEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0

    const mobileScrollContainer = document.getElementById(
      'mobile-scroll-container'
    )

    if (mobileScrollContainer) {
      mobileScrollContainer.scrollTop = 0
    }
  }, [pathname, selectedChapterId])
  const {
    chapterData,
    isLoading: isChapterViewLoading,
    hasError: hasChapterViewError,
    refetch: refetchChapterView,
    setChapterSrqRedDotsVisible
  } = useQuizDiagnosticsChapterViewData(chapterViewChapterId)

  const shouldShowNoAccess = !isLoading && !hasError && !srqEnabled

  const shouldShowEmptyState =
    hasError ||
    (!isLoading && isAllChaptersSelected && srqEnabled && summary.isEmpty)

  if (isUnsupportedViewport) {
    return null
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoaderContainer>
          <BouncingLoader />
        </LoaderContainer>
      )
    }

    if (shouldShowNoAccess) {
      return <QuizDiagnosticsNoAccessState />
    }

    if (shouldShowEmptyState) {
      return <QuizDiagnosticsEmptyState />
    }

    return (
      <>
        <QuizDiagnosticsHeader
          quizCount={
            isAllChaptersSelected
              ? summary.totalQuizzes
              : chapterData.totalQuizzesTaken
          }
          filterOptions={chapterFilterOptions}
          isChapterView={!isAllChaptersSelected}
          chapterQuizContext={
            isAllChaptersSelected
              ? null
              : {
                  bookTitle: chapterData.bookTitle,
                  chapterOrder: chapterData.chapterOrder
                }
          }
          chapterId={chapterData.chapterId}
          tag={chapterData.tag}
          chapterOrder={chapterData.chapterOrder}
          sections={chapterData.sections}
          chapterSrqRedDotsVisible={chapterData.chapterSrqRedDotsVisible}
          onChapterVisibilityChange={setChapterSrqRedDotsVisible}
          onAfterRedDotsToggle={() => {
            refetchChapterAverages()
            if (!isAllChaptersSelected) {
              refetchChapterView()
            }
          }}
        />
        {isAllChaptersSelected ? (
          <QuizDiagnosticsChaptersGrid
            bookGroups={bookGroups}
            overallPercent={summary.overallPercent}
          />
        ) : (
          <QuizDiagnosticsChapterSectionsGrid
            chapterData={chapterData}
            hasError={hasChapterViewError}
            isLoading={isChapterViewLoading}
            onRefetch={refetchChapterView}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {t(
            hasChapterInQuery
              ? 'pages.sectionAverages'
              : 'pages.quizDiagnostics'
          )}
        </title>
      </Helmet>
      <br />
      <PageContainer>{renderContent()}</PageContainer>
    </>
  )
}

export default QuizDiagnostics

const PageContainer = styled.div`
  width: 100%;
  max-width: ${QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH}px;
  margin: 0 auto;
  box-sizing: border-box;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;
  margin-top: 16px;
`
